/* BYOK API key storage — backend-synced, sessionStorage cached.
 *
 * Previously: keys lived in localStorage only. They never traveled with the
 * user — every browser/device meant re-pasting eight provider keys.
 *
 * Now:
 *   - Source of truth = /api/keys/* on lopettia-auth (Fernet-encrypted at
 *     rest, per-user salt).
 *   - On boot, after auth, we call POST /api/keys/reveal once to populate
 *     sessionStorage with decrypted plaintext.
 *   - All reads (provider adapters, settings drawer) go through getKey()
 *     which reads sessionStorage synchronously.
 *   - Saves go to the backend AND mirror to sessionStorage so the UI
 *     reflects immediately.
 *   - sessionStorage (not localStorage) so closing the tab clears the
 *     decrypted material — XSS in another tab can't read it.
 *   - Anonymous users still get the old localStorage fallback (no backend
 *     to sync to). On first login we migrate localStorage→backend and
 *     clear localStorage.
 */

import { PROVIDERS } from '../data/providers';

const SS_PREFIX = 'lp_aiboard_sskey_';
const LS_PREFIX_OLD = 'lp_aiboard_key_'; // legacy localStorage keys

type ProviderId = keyof typeof PROVIDERS;

// ---------- Synchronous accessors used everywhere ----------

/** Read a provider key. Returns "" if not set. Synchronous. */
export function getKey(providerId: string): string {
  // sessionStorage first (authoritative for logged-in sessions).
  try {
    const v = sessionStorage.getItem(SS_PREFIX + providerId);
    if (v) return v;
  } catch { /* sessionStorage disabled — fall through to localStorage */ }

  // localStorage fallback for anonymous users + legacy migration window.
  try {
    const v = localStorage.getItem(LS_PREFIX_OLD + providerId);
    if (v) return v;
  } catch { /* both storages disabled — give up */ }

  return '';
}

/** Set a provider key locally (sessionStorage) without touching the backend.
 *  Useful for the settings drawer where we want the local cache to reflect
 *  every keystroke immediately, but only PUT to the backend on debounce. */
export function setKeyLocal(providerId: string, value: string): void {
  setLocal(providerId, value);
}

/** Set a provider key locally (sessionStorage). Does NOT call the backend —
 *  use saveKey() for the user-action path that should sync. */
function setLocal(providerId: string, value: string): void {
  try {
    if (value) sessionStorage.setItem(SS_PREFIX + providerId, value);
    else sessionStorage.removeItem(SS_PREFIX + providerId);
  } catch { /* sessionStorage disabled — silently no-op */ }
}

/** Remove a provider key locally (both storages). */
function removeLocal(providerId: string): void {
  try { sessionStorage.removeItem(SS_PREFIX + providerId); } catch {}
  try { localStorage.removeItem(LS_PREFIX_OLD + providerId); } catch {}
}

// ---------- Backend sync ----------

interface RevealRow { provider: string; key: string; }
interface RevealResp { keys: RevealRow[]; }
interface KeyRow { provider: string; key_hint: string; updated_at: string; }
interface KeyListResp { keys: KeyRow[]; }

async function http<T>(
  path: string,
  init: RequestInit = {},
): Promise<{ ok: true; data: T } | { ok: false; status: number; message: string }> {
  try {
    const res = await fetch(`/api/keys${path}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(init.headers || {}),
      },
      ...init,
    });
    if (res.status === 204) return { ok: true, data: undefined as any };
    const text = await res.text();
    let body: any = null;
    if (text) { try { body = JSON.parse(text); } catch { body = text; } }
    if (!res.ok) {
      const msg =
        (body && typeof body === 'object' && (body.detail?.message || body.detail)) ||
        (typeof body === 'string' ? body : `HTTP ${res.status}`);
      return { ok: false, status: res.status, message: String(msg) };
    }
    return { ok: true, data: body as T };
  } catch (err: any) {
    return { ok: false, status: 0, message: err?.message || 'Network error' };
  }
}

/** Pull every stored key from the backend into sessionStorage. Idempotent.
 *  Called once at boot after we know the user is logged in. */
export async function hydrateFromBackend(): Promise<void> {
  const res = await http<RevealResp>('/reveal', { method: 'POST' });
  if (!res.ok) {
    if (res.status !== 401 && res.status !== 503) {
      console.warn('[api-keys] reveal failed:', res.status, res.message);
    }
    return;
  }
  for (const row of res.data.keys || []) {
    setLocal(row.provider, row.key);
  }
}

/** Save a key both locally and to the backend. Returns true on backend
 *  success (false on auth/network failure — caller may toast). The local
 *  copy is updated either way so the UI reflects immediately. */
export async function saveKey(providerId: string, plaintext: string): Promise<boolean> {
  const value = (plaintext || '').trim();
  if (!value) {
    return removeKey(providerId);
  }
  setLocal(providerId, value);
  const res = await http(`/${encodeURIComponent(providerId)}`, {
    method: 'PUT',
    body: JSON.stringify({ key: value }),
  });
  if (!res.ok && res.status !== 401) {
    console.warn(`[api-keys] save ${providerId} failed:`, res.status, res.message);
  }
  return res.ok;
}

/** Delete a key both locally and on the backend. */
export async function removeKey(providerId: string): Promise<boolean> {
  removeLocal(providerId);
  const res = await http(`/${encodeURIComponent(providerId)}`, { method: 'DELETE' });
  if (!res.ok && res.status !== 401) {
    console.warn(`[api-keys] delete ${providerId} failed:`, res.status, res.message);
  }
  return res.ok;
}

/** Clear every provider key locally + on backend. Used by the settings
 *  drawer's "Clear all keys" button. */
export async function clearAllKeys(): Promise<void> {
  const providers = Object.keys(PROVIDERS) as ProviderId[];
  // Local first for immediate UI feedback.
  for (const p of providers) removeLocal(p);
  // Then backend, in parallel.
  await Promise.all(
    providers.map((p) =>
      http(`/${encodeURIComponent(p)}`, { method: 'DELETE' }).then(() => undefined),
    ),
  );
}

// ---------- Migration: localStorage → backend ----------

/** One-shot: if the user has BYOK keys in legacy localStorage and is now
 *  logged in, push them to the backend and clear localStorage. Runs at
 *  most once per session. */
export async function migrateLegacyKeys(): Promise<void> {
  const providers = Object.keys(PROVIDERS) as ProviderId[];
  const found: Array<{ provider: string; key: string }> = [];
  for (const p of providers) {
    let val: string | null = null;
    try { val = localStorage.getItem(LS_PREFIX_OLD + p); } catch {}
    if (val) found.push({ provider: p, key: val });
  }
  if (!found.length) return;

  // Push each to backend in parallel. Drop the localStorage copy only
  // on successful upload — if upload fails (offline, 503), keep the
  // localStorage copy so the user doesn't lose their keys.
  await Promise.all(found.map(async ({ provider, key }) => {
    const res = await http(`/${encodeURIComponent(provider)}`, {
      method: 'PUT',
      body: JSON.stringify({ key }),
    });
    if (res.ok) {
      setLocal(provider, key);
      try { localStorage.removeItem(LS_PREFIX_OLD + provider); } catch {}
    }
  }));
}

// ---------- List (hints only) for settings UI ----------

export async function listKeyHints(): Promise<KeyRow[]> {
  const res = await http<KeyListResp>('', { method: 'GET' });
  if (!res.ok) return [];
  return res.data.keys || [];
}

// ---------- Boot hook ----------

let _hydrating = false;

/** Wire up automatic hydration of sessionStorage from the backend whenever
 *  the user transitions from null → logged-in. Called once at boot from
 *  main.ts. */
export function attachApiKeySync(): void {
  // Hydrate on every user-changed event when there's a user. Idempotent
  // by design — calling /reveal twice is harmless (rate-limited at 10/min).
  let lastUserId: string | null = null;
  window.addEventListener('lp-user-changed', async (e: any) => {
    const u = e?.detail;
    const uid = u?.id || null;
    if (!uid) {
      // Logged out — clear sessionStorage so a different user logging in on
      // the same browser can't inherit the previous user's keys.
      for (const p of Object.keys(PROVIDERS)) {
        try { sessionStorage.removeItem(SS_PREFIX + p); } catch {}
      }
      lastUserId = null;
      // Notify the UI so it re-renders the (now-empty) key rows.
      window.dispatchEvent(new CustomEvent('lp-api-keys-hydrated'));
      return;
    }
    if (uid === lastUserId || _hydrating) return;
    lastUserId = uid;
    _hydrating = true;
    try {
      // Migrate any legacy localStorage keys first (one-shot per user),
      // then pull whatever the backend has.
      await migrateLegacyKeys();
      await hydrateFromBackend();
      // Notify the settings drawer if it's open so it re-renders with the
      // freshly-hydrated values.
      window.dispatchEvent(new CustomEvent('lp-api-keys-hydrated'));
    } finally {
      _hydrating = false;
    }
  });
}
